import { IBlogType } from '@introspection/index';
import { Component, css, html, LitElement, property } from '@rxdi/lit-html';
import { ArrowIcon, StudioStyles } from '@shared/styles/studio.styles';

/** Plain-text excerpt of a markdown post. */
export function excerpt(markdown: string, max = 170): string {
  const text = (markdown ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/[#>*_`~|-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.slice(0, max).replace(/\s\S*$/, '')}…` : text;
}

/** "27 Dec 2025" from a timestamp (ms, as a number or string) or a date string. */
export function postDate(value: string | number): string {
  const date = new Date(Number(value) || value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Minutes to read, at ~220 words per minute. */
export function readingTime(markdown: string): number {
  const words = (markdown ?? '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

/** URL of a post page. */
export const postUrl = (post: IBlogType) => `/blog/${post.category?.searchKey ?? 'all'}/${post.searchKey}`;

/**
 * <rs-post-card> - one article as a card (home journal and the blog).
 * Pure UI: takes the post, emits `open` when clicked.
 */
@Component({
  selector: 'rs-post-card',
  styles: [
    StudioStyles,
    css`
      :host {
        display: block;
        height: 100%;
      }

      a.card {
        display: flex;
        flex-direction: column;
        gap: 14px;
        height: 100%;
        min-height: 280px;
        padding: 28px;
        cursor: pointer;
        transition: transform 0.35s var(--rs-ease), border-color 0.35s var(--rs-ease);
      }

      a.card:hover {
        transform: translateY(-4px);
        border-color: var(--rs-line-strong);
      }

      :host([featured]) a.card {
        min-height: 360px;
        padding: clamp(28px, 4vw, 48px);
        background: radial-gradient(60% 80% at 100% 0%, var(--rs-plasma-soft), transparent 60%), var(--rs-gradient-card),
          var(--rs-surface);
      }

      .meta {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--rs-text-muted);
      }

      .cat {
        color: var(--rs-signal);
      }

      .draft {
        color: var(--rs-ember);
      }

      h3 {
        margin: 0;
        font-family: var(--rs-font-display);
        font-size: 22px;
        line-height: 1.2;
        letter-spacing: -0.02em;
        text-wrap: balance;
      }

      :host([featured]) h3 {
        font-size: clamp(28px, 3.4vw, 44px);
        line-height: 1.05;
        letter-spacing: -0.03em;
        max-width: 22ch;
      }

      p {
        margin: 0;
        color: var(--rs-text-2);
        font-size: 15px;
        line-height: 1.6;
      }

      :host([featured]) p {
        font-size: 17px;
        max-width: 60ch;
      }

      .more {
        margin-top: auto;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
        color: var(--rs-text);
      }

      .more svg {
        width: 14px;
        height: 14px;
        transition: transform 0.3s var(--rs-ease);
      }

      a.card:hover .more svg {
        transform: translateX(4px);
      }
    `,
  ],
  template(this: PostCardComponent) {
    const post = this.post;
    if (!post) {
      return html``;
    }
    return html`
      <a class="card" href=${postUrl(post)} @click=${(e: Event) => this.open(e)}>
        <div class="meta">
          <span class="cat">${post.category?.name ?? 'Article'}</span>
          <span>${postDate(post.createdAt)}</span>
          <span>${readingTime(post.content)} min read</span>
          ${post.draft ? html`<span class="draft">draft</span>` : ''}
        </div>
        <h3>${post.title}</h3>
        <p>${excerpt(post.content, this.featured ? 280 : 170)}</p>
        <span class="more">Read ${ArrowIcon}</span>
      </a>
    `;
  },
})
export class PostCardComponent extends LitElement {
  @property({ attribute: false }) post: IBlogType;
  @property({ type: Boolean, reflect: true }) featured = false;

  private open(event: Event) {
    event.preventDefault();
    this.dispatchEvent(new CustomEvent('open', { detail: this.post }));
  }
}
